import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiJson, apiSend } from "@/api/client";
import { segment, withQuery, type SaveVariables } from "@/api/query";
import { reportKeys } from "@/api/reports";

/* Mirrors api/src/pbo_invoicing/books/schemas.py. Money is strings ("1400.00"), rates
   strings with six decimals. Entries are append-only: a mistake is a reversal. */

export type AccountKind = "asset" | "liability" | "equity" | "income" | "expense";
export const ACCOUNT_KINDS: { value: AccountKind; label: string }[] = [
  { value: "asset", label: "Asset" },
  { value: "liability", label: "Liability" },
  { value: "equity", label: "Equity" },
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
];
export type EntryKind = "auto" | "manual" | "reversal" | "opening";
export type PartyKind = "" | "client" | "vendor" | "person";
export type PendingReason = "rate_missing" | "waits_for" | "error";

export type LedgerAccountInput = {
  code: string;
  name: string;
  kind: AccountKind;
  schedule_group: string;
  archived: boolean;
};

export type LedgerAccount = LedgerAccountInput & {
  id: string;
  system_key: string | null;
  swings: boolean;
  /** Debit balance positive, credit balance negative. */
  balance_inr: string;
  version: number;
};

export function pickAccountInput(account: LedgerAccount): LedgerAccountInput {
  const { code, name, kind, schedule_group, archived } = account;
  return { code, name, kind, schedule_group, archived };
}

export type ExchangeRateInput = {
  on: string;
  currency: string;
  rate_inr: string;
  source: string;
};

export type ExchangeRate = ExchangeRateInput & {
  id: string;
  typed_by_username: string | null;
  version: number;
};

export type JournalLineInput = {
  account_id: string;
  debit_inr: string;
  credit_inr: string;
  party_kind: PartyKind;
  party_id: string | null;
  party_label: string;
  fx_currency: string;
  fx_amount: string | null;
  fx_rate: string | null;
  memo: string;
};

export type JournalInput = {
  entry_date: string;
  narration: string;
  reference: string;
  lines: JournalLineInput[];
};

export type JournalLine = Omit<JournalLineInput, "account_id"> & {
  id: string;
  line_no: number;
  account_id: string;
  account_code: string;
  account_name: string;
};

export type JournalRow = {
  id: string;
  entry_no: number;
  label: string;
  entry_date: string;
  fy_code: string;
  kind: EntryKind;
  narration: string;
  reference: string;
  source_kind: string | null;
  source_id: string | null;
  source_label: string | null;
  /** The invoice whose page shows the record: an invoice itself, a payment's invoice. */
  source_invoice_id: string | null;
  reverses_entry_id: string | null;
  superseded_by_entry_id: string | null;
  posted_by_username: string | null;
  created_at: string;
  total_inr: string;
};

export type Journal = JournalRow & { lines: JournalLine[] };

export type Pending = {
  source_kind: string;
  source_id: string;
  label: string;
  reason: PendingReason;
  currency: string | null;
  on: string | null;
  detail: string;
  source_invoice_id: string | null;
};

/** A record that is in the books, and the entry that books it. */
export type Booked = {
  source_kind: string;
  source_id: string;
  entry_id: string;
  label: string;
  entry_date: string;
};

export type PostResult = { posted: number; reversed: number; pending: Pending[] };

export type Overview = {
  locked_through: string | null;
  income_tax_percent: string;
  fy_code: string;
  entries: number;
  last_posted_at: string | null;
  pending: Pending[];
};

export type Period = { from: string; to: string };

export type TrialBalanceRow = {
  account_id: string;
  code: string;
  name: string;
  kind: AccountKind;
  schedule_group: string;
  opening_inr: string;
  debit_inr: string;
  credit_inr: string;
  closing_inr: string;
};

export type TrialBalance = {
  since: string;
  until: string;
  rows: TrialBalanceRow[];
  total_opening_inr: string;
  total_debit_inr: string;
  total_credit_inr: string;
  total_closing_inr: string;
};

export type GroupLine = { account_id: string; code: string; name: string; amount_inr: string };
export type Group = { group: string; lines: GroupLine[]; total_inr: string };

export type ProfitLoss = {
  since: string;
  until: string;
  income: Group[];
  expenses: Group[];
  total_income_inr: string;
  total_expenses_inr: string;
  net_profit_inr: string;
  tax: {
    percent: string;
    estimated_tax_inr: string;
    fy_code: string;
    schedule: { due_on: string; percent: number; cumulative_inr: string }[];
  };
};

export type BalanceSheet = {
  as_of: string;
  assets: Group[];
  liabilities: Group[];
  equity: Group[];
  current_surplus_inr: string;
  total_assets_inr: string;
  total_liabilities_and_equity_inr: string;
  balanced: boolean;
};

export type LedgerRow = {
  entry_id: string;
  label: string;
  entry_date: string;
  kind: EntryKind;
  narration: string;
  source_kind: string | null;
  source_id: string | null;
  source_label: string | null;
  source_invoice_id: string | null;
  party_label: string;
  debit_inr: string;
  credit_inr: string;
  balance_inr: string;
  fx_currency: string;
  fx_amount: string | null;
  fx_rate: string | null;
};

export type Ledger = {
  account: LedgerAccount;
  since: string;
  until: string;
  party_id: string | null;
  opening_inr: string;
  rows: LedgerRow[];
  closing_inr: string;
};

export type TdsRegister = {
  since: string;
  until: string;
  rows: {
    expense_id: string;
    spent_on: string;
    payee: string;
    description: string;
    section: string;
    amount_inr: string;
    tds_inr: string;
    status: string;
    deposit_due: string;
  }[];
  by_section: { section: string; amount_inr: string; tds_inr: string }[];
  by_month: { month: string; tds_inr: string; deposit_due: string }[];
};

export type GstRegister = {
  since: string;
  until: string;
  gstin: string;
  output: {
    invoice_id: string;
    invoice_no: string | null;
    invoice_date: string;
    client: string;
    currency: string;
    subtotal: string;
    tax: string;
    zero_rated: boolean;
    posted_inr: string | null;
  }[];
  input: {
    expense_id: string;
    spent_on: string;
    payee: string;
    description: string;
    amount_inr: string;
    gst_inr: string;
    itc_eligible: boolean;
  }[];
  total_output_tax_inr: string;
  total_input_gst_inr: string;
  total_itc_inr: string;
};

export type JournalFilters = {
  from?: string;
  to?: string;
  kind?: EntryKind;
  accountId?: string;
  q?: string;
  /** One record's entries (and their reversals), whatever the period. */
  sourceId?: string;
  limit?: number;
};

export type LedgerFilters = Period & { accountId: string; partyId?: string };

export const booksKeys = {
  all: ["books"] as const,
  overview: ["books", "overview"] as const,
  accounts: (includeArchived: boolean) => ["books", "accounts", includeArchived] as const,
  rates: (currency: string | undefined) => ["books", "rates", currency ?? "all"] as const,
  journals: (filters: JournalFilters) => ["books", "journals", filters] as const,
  journal: (id: string) => ["books", "journal", id] as const,
  booked: (sourceKind: string) => ["books", "booked", sourceKind] as const,
  trialBalance: (period: Period) => ["books", "trial-balance", period] as const,
  profitLoss: (period: Period) => ["books", "profit-loss", period] as const,
  balanceSheet: (asOf: string) => ["books", "balance-sheet", asOf] as const,
  ledger: (filters: LedgerFilters) => ["books", "ledger", filters] as const,
  tds: (period: Period) => ["books", "tds", period] as const,
  gst: (period: Period) => ["books", "gst", period] as const,
};

// --------------------------------------------------------------------------- requests

export function getOverview(): Promise<Overview> {
  return apiJson<Overview>("/books/overview");
}

export function postBooks(): Promise<PostResult> {
  return apiSend<PostResult>("/books/post", "POST");
}

export function listLedgerAccounts(includeArchived = false): Promise<LedgerAccount[]> {
  return apiJson<LedgerAccount[]>(
    withQuery("/books/accounts", { include_archived: includeArchived }),
  );
}

export function saveLedgerAccount({
  input,
  existing,
}: SaveVariables<LedgerAccountInput>): Promise<LedgerAccount> {
  return existing
    ? apiSend<LedgerAccount>(
        `/books/accounts/${segment(existing.id)}`,
        "PUT",
        input,
        existing.version,
      )
    : apiSend<LedgerAccount>("/books/accounts", "POST", input);
}

export function listRates(currency?: string): Promise<ExchangeRate[]> {
  return apiJson<ExchangeRate[]>(withQuery("/books/rates", { currency }));
}

export function saveRate({
  input,
  existing,
}: SaveVariables<ExchangeRateInput>): Promise<ExchangeRate> {
  return existing
    ? apiSend<ExchangeRate>(`/books/rates/${segment(existing.id)}`, "PUT", input, existing.version)
    : apiSend<ExchangeRate>("/books/rates", "POST", input);
}

export function listJournals(filters: JournalFilters): Promise<JournalRow[]> {
  return apiJson<JournalRow[]>(
    withQuery("/books/journals", {
      from: filters.from,
      to: filters.to,
      kind: filters.kind,
      account_id: filters.accountId,
      q: filters.q,
      source_id: filters.sourceId,
      limit: filters.limit,
    }),
  );
}

export function listBooked(sourceKind: string): Promise<Booked[]> {
  return apiJson<Booked[]>(withQuery("/books/booked", { source_kind: sourceKind }));
}

export function getJournal(id: string): Promise<Journal> {
  return apiJson<Journal>(`/books/journals/${segment(id)}`);
}

export function createJournal(input: JournalInput): Promise<Journal> {
  return apiSend<Journal>("/books/journals", "POST", input);
}

export function reverseJournal(id: string, body: { on: string; reason: string }): Promise<Journal> {
  return apiSend<Journal>(`/books/journals/${segment(id)}/reverse`, "POST", body);
}

export function repostJournal(id: string, body: { reason: string }): Promise<Journal> {
  return apiSend<Journal>(`/books/journals/${segment(id)}/repost`, "POST", body);
}

export function getTrialBalance(period: Period): Promise<TrialBalance> {
  return apiJson<TrialBalance>(withQuery("/books/trial-balance", period));
}

export function getProfitLoss(period: Period): Promise<ProfitLoss> {
  return apiJson<ProfitLoss>(withQuery("/books/profit-loss", period));
}

export function getBalanceSheet(asOf: string): Promise<BalanceSheet> {
  return apiJson<BalanceSheet>(withQuery("/books/balance-sheet", { as_of: asOf }));
}

export function getLedger(filters: LedgerFilters): Promise<Ledger> {
  return apiJson<Ledger>(
    withQuery("/books/ledger", {
      account_id: filters.accountId,
      from: filters.from,
      to: filters.to,
      party_id: filters.partyId,
    }),
  );
}

export function getTdsRegister(period: Period): Promise<TdsRegister> {
  return apiJson<TdsRegister>(withQuery("/books/tds-register", period));
}

export function getGstRegister(period: Period): Promise<GstRegister> {
  return apiJson<GstRegister>(withQuery("/books/gst-register", period));
}

/** The day book workbook, with the file name the API chose. */
export async function fetchDayBook(period: Period): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(withQuery("/books/day-book.xlsx", period));
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return { blob: await res.blob(), filename: match?.[1] ?? "day-book.xlsx" };
}

// --------------------------------------------------------------------------- hooks

export function useOverview() {
  return useQuery({ queryKey: booksKeys.overview, queryFn: getOverview });
}

export function useLedgerAccounts(includeArchived = false) {
  return useQuery({
    queryKey: booksKeys.accounts(includeArchived),
    queryFn: () => listLedgerAccounts(includeArchived),
  });
}

export function useRates(currency?: string) {
  return useQuery({ queryKey: booksKeys.rates(currency), queryFn: () => listRates(currency) });
}

export function useJournals(filters: JournalFilters) {
  return useQuery({ queryKey: booksKeys.journals(filters), queryFn: () => listJournals(filters) });
}

/** Which records of a kind are in the books; one request however many rows ask. */
export function useBooked(sourceKind: string) {
  return useQuery({ queryKey: booksKeys.booked(sourceKind), queryFn: () => listBooked(sourceKind) });
}

export function useJournal(id: string | null) {
  return useQuery({
    queryKey: booksKeys.journal(id ?? ""),
    queryFn: () => getJournal(id ?? ""),
    enabled: id !== null,
  });
}

export function useTrialBalance(period: Period) {
  return useQuery({
    queryKey: booksKeys.trialBalance(period),
    queryFn: () => getTrialBalance(period),
  });
}

export function useProfitLoss(period: Period) {
  return useQuery({ queryKey: booksKeys.profitLoss(period), queryFn: () => getProfitLoss(period) });
}

export function useBalanceSheet(asOf: string) {
  return useQuery({ queryKey: booksKeys.balanceSheet(asOf), queryFn: () => getBalanceSheet(asOf) });
}

export function useLedger(filters: LedgerFilters | null) {
  return useQuery({
    queryKey: booksKeys.ledger(filters ?? { accountId: "", from: "", to: "" }),
    queryFn: () => getLedger(filters as LedgerFilters),
    enabled: filters !== null,
  });
}

export function useTdsRegister(period: Period) {
  return useQuery({ queryKey: booksKeys.tds(period), queryFn: () => getTdsRegister(period) });
}

export function useGstRegister(period: Period) {
  return useQuery({ queryKey: booksKeys.gst(period), queryFn: () => getGstRegister(period) });
}

/** Anything written to the books changes every statement and the reports. */
function useBooksMutation<Variables, Result>(
  mutationFn: (variables: Variables) => Promise<Result>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all(
        [booksKeys.all, reportKeys.all].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

export function usePostBooks() {
  return useBooksMutation(() => postBooks());
}

export function useSaveLedgerAccount() {
  return useBooksMutation(saveLedgerAccount);
}

export function useSaveRate() {
  return useBooksMutation(saveRate);
}

export function useCreateJournal() {
  return useBooksMutation(createJournal);
}

export function useReverseJournal() {
  return useBooksMutation(({ id, on, reason }: { id: string; on: string; reason: string }) =>
    reverseJournal(id, { on, reason }),
  );
}

export function useRepostJournal() {
  return useBooksMutation(({ id, reason }: { id: string; reason: string }) =>
    repostJournal(id, { reason }),
  );
}
